import React, {useCallback, useEffect, useState} from 'react';
import axios from 'axios';
import {useTranslation} from 'react-i18next';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Button} from 'primereact/button';
import {Dialog} from 'primereact/dialog';
import {Dropdown} from 'primereact/dropdown';
import {TreeSelect} from 'primereact/treeselect';
import {InputNumber} from 'primereact/inputnumber';
import {Message} from 'primereact/message';
import {Skeleton} from 'primereact/skeleton';

export function allocationTree(items, parent = null, visited = []) {
  return items.filter(item => (item.parent_id ?? null) === parent && !visited.includes(item.id)).map(item => ({
    key: String(item.id), label: item.name, selectable: item.active !== false,
    children: allocationTree(items, item.id, [...visited, item.id]),
  }));
}
const headers = () => ({Authorization: 'Bearer ' + localStorage.getItem('userToken'), 'App-Network': localStorage.getItem('networkId')});

export function summarizeAllocations(data) {
  const destinations = new Map();
  const allocations = [...data.items.flatMap(item => item.allocations), ...data.unlinked];
  allocations.forEach(allocation => {
    const id = allocation.destination_id == null ? 'unassigned' : String(allocation.destination_id);
    if (!destinations.has(id)) destinations.set(id, {id, destination: allocation.destination, amount: 0});
    destinations.get(id).amount += Math.round(Number(allocation.amount || 0) * 100);
  });
  return Array.from(destinations.values(), row => ({...row, amount: row.amount / 100}));
}

export default function ReceiptAllocationManager({receiptId, onChanged}) {
  const {t} = useTranslation('common');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [selection, setSelection] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [destination, setDestination] = useState(null);
  const [costType, setCostType] = useState(null);
  const [value, setValue] = useState(100);
  const endpoint = `${process.env.REACT_APP_API_HOST}/api/v2/receipt/${receiptId}/allocations`;
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { const response = await axios.get(endpoint, {headers: headers()}); setData(response.data.data); setSelection([]); }
    catch (e) { setData(null); setError(e.response?.data?.message || t('toastError')); }
    finally { setLoading(false); }
  }, [endpoint, t]);
  useEffect(() => { load(); }, [load]);
  const format = amount => window.App.currencyFormatter(data?.currency || 'HUF', Number(amount || 0));
  const percentage = amount => data?.amount ? `${(100 * amount / data.amount).toLocaleString('hu-HU', {maximumFractionDigits: 2})}%` : '—';
  const availableForItem = item => {
    const budget = item.remaining >= 0 ? data.remaining_positive : data.remaining_negative;
    return Math.sign(item.remaining) * Math.min(Math.abs(item.remaining), Math.max(0, Math.sign(item.remaining) * budget));
  };
  const open = (items, allocation = null) => {
    setError(null); setDialog({items, allocation}); setDestination(allocation?.destination_id || null);
    setCostType(allocation?.cost_type_id || null);
    const remaining = items.length === 1 ? availableForItem(items[0]) : 0;
    setValue(allocation ? allocation.amount : items.length === 1 ? remaining : 100);
  };
  const save = async (payload) => {
    setBusy(true); setError(null);
    try {
      await axios.post(endpoint, payload, {headers: headers()}); setDialog(null);
      await load(); onChanged?.();
    } catch (e) { setError(e.response?.data?.message || t('toastError')); }
    finally { setBusy(false); }
  };
  const allocationRow = (allocation, item) => <div key={allocation.id} className="p-d-flex p-ai-center p-jc-between p-mb-2" style={{gap: '.75rem'}}>
    <span>{allocation.destination || '—'}{allocation.cost_type ? ` • ${allocation.cost_type}` : ''} — {format(allocation.amount)}
      {!allocation.editable && <small className="p-ml-2">{t('receiptAllocations.automatic')} ({allocation.source})</small>}
    </span>
    {data.can_write && allocation.editable && item && <span className="p-d-flex">
      <Button icon="pi pi-pencil" className="p-button-text" aria-label={t('edit')} disabled={busy} onClick={() => open([item], allocation)} />
      <Button icon="pi pi-trash" className="p-button-text p-button-danger" aria-label={t('delete')} disabled={busy}
        onClick={() => { if (window.confirm(t('confirmDelete'))) save({allocation_id: allocation.id, action: 'delete'}); }} />
    </span>}
  </div>;
  if (loading && !data) return <Skeleton height="12rem" />;
  if (!data) return <Message severity="error" text={error || t('toastError')} />;
  return <>
    <div className="p-grid p-mb-3">
      {['amount', 'allocated', 'remaining'].map(key => <div className="p-col-12 p-md-4" key={key}>
        <div className="p-p-3" style={{background: 'var(--surface-b)', borderRadius: 6}}>
          <div>{t(`receiptAllocations.${key}`)}</div>
          {key === 'remaining' && data.fully_allocated ? <strong>{t(`receiptAllocations.all_${data.kind}`)}</strong>
            : key === 'allocated' && !data.has_allocations ? <strong>{t('receiptAllocations.none')}</strong>
            : <><strong>{format(data[key])}</strong><span className="p-ml-2">{percentage(data[key])}</span></>}
        </div>
      </div>)}
    </div>
    <section className="p-mb-4" aria-label={t('receiptAllocationSummary.title')}>
      <h4 className="p-mt-0 p-mb-3">{t('receiptAllocationSummary.title')}</h4>
      <DataTable value={summarizeAllocations(data)} dataKey="id" loading={loading}
        responsiveLayout="scroll" autoLayout stripedRows emptyMessage={t('receiptAllocationSummary.empty')}>
        <Column field="destination" header={t(data.kind === 'cost' ? 'fieldCostCenter' : 'fieldProfitCenter')}
          body={row => row.destination || t('receiptAllocationSummary.unknown')} footer={t('receiptAllocationSummary.total')} />
        <Column header={t('receiptAllocationSummary.amount')} body={row => format(row.amount)}
          className="p-text-right" alignHeader="right" footer={format(data.allocated)} footerClassName="p-text-right" />
      </DataTable>
    </section>
    {error && !dialog && <Message severity="error" text={error} />}
    {data.can_write && <div className="p-d-flex p-mb-3" style={{gap: '.5rem', flexWrap: 'wrap'}}>
      <Button label={t('receiptAllocations.whole')} icon="pi pi-percentage" disabled={busy || data.fully_allocated || !data.items.length} onClick={() => open(data.items)} />
      <Button label={t('receiptAllocations.selected')} icon="pi pi-list" disabled={busy || !selection.length} onClick={() => open(selection)} />
    </div>}
    <DataTable value={data.items} dataKey="id" selection={selection} onSelectionChange={e => setSelection(e.value)} loading={loading}
      responsiveLayout="scroll" autoLayout stripedRows emptyMessage={t('noResults')}>
      {data.can_write && <Column selectionMode="multiple" style={{width: '3rem'}} />}
      <Column field="name" header={t('fieldItemName')} />
      <Column header={t('receiptAllocations.amount')} body={row => format(row.amount)} className="p-text-right" alignHeader="right" />
      <Column header={t('receiptAllocations.allocated')} body={row => format(row.allocated)} className="p-text-right" alignHeader="right" />
      <Column header={t('receiptAllocations.remaining')} body={row => format(row.remaining)} className="p-text-right" alignHeader="right" />
      <Column header={t('receiptAllocations.details')} body={row => <>{row.allocations.map(a => allocationRow(a, row))}
        {data.can_write && Math.abs(row.remaining) >= .005 && <Button label={t('receiptAllocations.allocate')} className="p-button-text" icon="pi pi-plus" disabled={busy} onClick={() => open([row])} />}</>} />
    </DataTable>
    {data.unlinked.length > 0 && <div className="p-mt-3"><h4>{t('receiptAllocations.unlinked')}</h4>{data.unlinked.map(a => allocationRow(a, null))}</div>}
    <Dialog visible={!!dialog} onHide={() => !busy && setDialog(null)} header={dialog?.items.length === 1
      ? t('receiptAllocations.singleTitle', {name: dialog.items[0].name})
      : t('receiptAllocations.multipleTitle', {count: dialog?.items.length || 0})} style={{width: '38rem', maxWidth: '95vw'}} modal>
      {dialog && <div className="p-fluid">
        {error && <Message severity="error" text={error} />}
        <div className="p-field p-mt-3 p-mb-3"><label className="p-d-block p-mb-2" htmlFor="allocation-destination">{t(data.kind === 'cost' ? 'fieldCostCenter' : 'fieldProfitCenter')}</label>
          <TreeSelect inputId="allocation-destination" value={destination ? String(destination) : null} options={allocationTree(data.destinations)}
            selectionMode="single" filter onChange={e => setDestination(Number(e.value))} />
        </div>
        {data.kind === 'cost' && <div className="p-field p-mb-3"><label className="p-d-block p-mb-2" htmlFor="allocation-cost-type">{t('fieldCostType')}</label>
          <Dropdown inputId="allocation-cost-type" value={costType} options={data.cost_types} optionLabel="name" optionValue="id" filter onChange={e => setCostType(e.value)} /></div>}
        <div className="p-field p-mb-3"><label className="p-d-block p-mb-2" htmlFor="allocation-amount">{t(dialog.items.length === 1 ? 'columnAmount' : 'receiptAllocations.remainingPercent')}</label>
          <InputNumber inputId="allocation-amount" value={value} onChange={e => setValue(e.value)} onValueChange={e => setValue(e.value)} minFractionDigits={0} maxFractionDigits={2} suffix={dialog.items.length > 1 ? ' %' : undefined} />
          {dialog.items.length === 1 && <>
            <small className="p-d-block p-mt-2">{t('receiptAllocations.availableAmount')}: {format(availableForItem(dialog.items[0]) + (dialog.allocation?.amount || 0))}</small>
            <small className="p-d-block p-mt-1">{t('receiptAllocations.remainingAfterSave')}: {format(availableForItem(dialog.items[0]) + (dialog.allocation?.amount || 0) - (value || 0))}</small>
          </>}
        </div>
        <div className="p-d-flex p-jc-end p-pt-3" style={{gap: '.75rem'}}>
        <Button label={t('cancel')} className="p-button-text" style={{width: 'auto'}} disabled={busy} onClick={() => setDialog(null)} />
        <Button label={t('save')} style={{width: 'auto'}} icon="pi pi-check" loading={busy} disabled={!destination || !value || (data.kind === 'cost' && !costType)} onClick={() => save({
          item_ids: dialog.items.map(item => item.id), allocation_id: dialog.allocation?.id,
          destination_id: destination, cost_type_id: costType, [dialog.items.length === 1 ? 'amount' : 'percent']: value,
        })} />
        </div>
      </div>}
    </Dialog>
  </>;
}
